import './Button.css'
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'
import type { IconName } from '../graphics/iconsAvailable'
import { Icon } from '../graphics/Icon'
import { cn } from '@/utilities/misc'
import { Spinner } from '../graphics/Spinner'
import { Tooltip } from './Tooltip'
import type { Placement } from '@floating-ui/dom'
import { Link } from 'react-router-dom'

export type ButtonVariant = 'primary' | 'secondary' | 'raw'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  label?: ReactNode
  icon?: IconName
  iconPosition?: 'left' | 'right'
  tooltip?: ReactNode
  tooltipPlacement?: Placement
  href?: string
  target?: string
  busy?: boolean
  disabled?: boolean
  children?: ReactNode
}

export function Button({
  variant,
  label,
  icon,
  iconPosition = 'left',
  tooltip,
  tooltipPlacement,
  href,
  target,
  busy,
  disabled,
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {

  //--------------------------------------------------------------------------------
  className = cn(
    'Button',
    variant && 'Variant' + (variant as string).replace(/^[a-z]/, m => m.toUpperCase()),
    busy && 'Busy',
    disabled && 'Disabled',
    className,
  )

  //--------------------------------------------------------------------------------
  function handleClick(ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) {
    if (disabled) {
      ev.preventDefault()
      ev.stopPropagation()
      return
    }
    onClick?.(ev as any)
  }

  //--------------------------------------------------------------------------------
  let guts = <>{children ??label}</>

  if (busy) {
    guts = (<>
      <span className='Label'>
        <span>{guts}</span>
      </span>
      <Spinner />
    </>)

  } else if (icon) {
    if (iconPosition === 'right') {
      guts = (
        <span className='Label'>
          <span>{guts}</span>
          <Icon icon={icon} />
        </span>
      )
    } else {
      guts = (
        <span className='Label'>
          <Icon icon={icon} />
          <span>{guts}</span>
        </span>
      )
    }
  }

  //--------------------------------------------------------------------------------
  // Render

  if (href) return (
    <Link
      to={href ?? '?'}
      target={target}
      className={className}
      onClick={handleClick}
      {...rest as any}
    >
      <Tooltip placement={tooltipPlacement} disabled={disabled}>
        {tooltip}
      </Tooltip>
      <span className='Inliner' />
      {guts}
    </Link>
  )

  return (
    <button
      className={className}
      disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      <Tooltip placement={tooltipPlacement} disabled={disabled}>
        {tooltip}
      </Tooltip>
      <span className='Inliner' />
      {guts}
    </button>
  )
}
